/** Dashboards per role (codebase doc §11: /app/student, /app/teacher, /app/guardian, /app/admin, /app/super-admin). */
export const DASHBOARDS = ['super-admin', 'admin', 'teacher', 'guardian', 'student'] as const;
export type Dashboard = (typeof DASHBOARDS)[number];

export const DASHBOARD_PATHS = {
  student: '/app/student',
  teacher: '/app/teacher',
  guardian: '/app/guardian',
  admin: '/app/admin',
  'super-admin': '/app/platform/organizations',
} as const satisfies Record<Dashboard, string>;

/** Staff roles that land on the role-filtered academy dashboard. */
export const ADMIN_DASHBOARD_ROLES = ['org_admin', 'coordinator', 'finance', 'support', 'content_manager'] as const;

export interface DashboardContext {
  roles: readonly string[];
  isSuperAdmin: boolean;
  /** organizations.kind of the active organization ("academy" | "independent_teacher"). */
  organizationKind?: string | null | undefined;
}

/** Every dashboard the user may open, most relevant first. */
export function dashboardsFor(ctx: DashboardContext): Dashboard[] {
  const has = (role: string) => ctx.roles.includes(role);
  const out: Dashboard[] = [];
  if (ctx.isSuperAdmin) out.push('super-admin');
  const isAdmin = ADMIN_DASHBOARD_ROLES.some(has);
  const isTeacher = has('teacher');
  // Solo mode: an independent teacher holds org_admin + finance + teacher but teaches first (brief D2).
  if (ctx.organizationKind === 'independent_teacher' && isTeacher) {
    out.push('teacher');
    if (isAdmin) out.push('admin');
  } else {
    if (isAdmin) out.push('admin');
    if (isTeacher) out.push('teacher');
  }
  if (has('guardian')) out.push('guardian');
  if (has('student')) out.push('student');
  return out;
}

export function primaryDashboard(ctx: DashboardContext): Dashboard | null {
  return dashboardsFor(ctx)[0] ?? null;
}

export function canOpenDashboard(dashboard: Dashboard, ctx: DashboardContext): boolean {
  return dashboardsFor(ctx).includes(dashboard);
}
